/* assets/lab-shipping-quote.js — the shipping cost in the product page's shipping line
   (snippets/lab-pdp-line-shipping.liquid). The snippet embeds, as JSON, the rate card ShopClaude
   copies every hour from Settings > Shipping (shop.metafields.shopclaude.shipping_rates), the
   variant's weight and price, the store's money format and the translated strings.

   The visitor picks a country from the card's countries (names from Intl.DisplayNames in the
   page language) in the popover of the country word, where each row already shows that
   country's cost for one item. A search field on top filters the rows by name (accents
   ignored, in the page language or English) or ISO code; Enter picks the first row left. The first choice is, in order: the one saved in localStorage, the country of
   the visitor's IP (Shopify's /browsing_context_suggestions.json, fetched once per page) unless
   that is Italy on a German or French page, then the page language's country (de → DE, fr → FR:
   a German page is not shown Italian rates), then the market country. Picking a country only
   changes this estimate, never the market or the currency.

   For quantities 1–8 the weight is weight × n and each zone's rate the cheapest band holding it,
   0 once the subtotal reaches the zone's free rate (only when the page is in the card's
   currency, since the threshold is in it). A country split into zones (Italy: mainland, islands
   and Calabria) shows lowest–highest; equal rows merge ("2–3"), the table stops at the first
   quantity free everywhere ("3+"). product-info.js replaces the whole line on a variant change,
   so a new element reads the new variant's weight and price.

   GA4 (G-L9FFD504B5, configured on the page by the Google & YouTube app's Google tag) gets
   shipping_countries_open, shipping_country_select and shipping_rates_open, each with
   shipping_country and item_id, through gtag with send_to, so Consent Mode applies as it does to
   every other hit (cookieless pings until the visitor accepts). The dataLayer shim queues a
   call made before gtag.js has loaded. */
if (!customElements.get('shipping-quote')) {
  const STORAGE_KEY = 'lab-shipping-country';
  const GA4 = 'G-L9FFD504B5';
  const track = (name, params) => {
    window.dataLayer = window.dataLayer || [];
    const gtag = typeof window.gtag === 'function' ? window.gtag : function () { window.dataLayer.push(arguments); };
    gtag('event', name, { ...params, send_to: GA4 });
  };
  const LANGUAGE_COUNTRY = { de: 'DE', fr: 'FR' };
  const MAX_QUANTITY = 8;
  let detected = null;
  const ENGLISH = new Intl.DisplayNames(['en'], { type: 'region' });
  /** Lower case without accents, so "osterreich" finds Österreich. */
  const fold = (text) => text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

  /** The IP country Shopify detects, once per page; null when it cannot say. */
  const detectCountry = () => {
    detected ??= fetch(`${window.Shopify?.routes?.root || '/'}browsing_context_suggestions.json`, { headers: { accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => body?.detected_values?.country?.handle || null)
      .catch((error) => {
        console.warn('[shipping-quote] country detection failed', error);
        return null;
      });
    return detected;
  };

  const readSaved = () => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null; // storage blocked (private mode, policy): the choice just is not remembered
    }
  };

  class ShippingQuote extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.hasPickedCountry = false;
      this.data = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      this.button = this.querySelector('[popovertarget]');
      this.name = this.querySelector('[data-country]');
      this.pop = this.querySelector('[popover]');
      this.list = this.pop.querySelector('ul');
      this.search = this.pop.querySelector('input[type="search"]');
      this.none = this.pop.querySelector('[data-none]');
      this.quote = this.querySelector('[data-quote]');
      this.free = this.querySelector('[data-free]');
      this.rates = this.querySelector('[data-rates]');
      this.body = this.querySelector('tbody');

      const { card, lang } = this.data;
      this.names = new Intl.DisplayNames([lang], { type: 'region' });
      this.codes = Object.keys(card.countries).sort((a, b) => this.names.of(a).localeCompare(this.names.of(b), lang));
      this.list.replaceChildren(...this.codes.map((code) => this.row(code)));
      this.pop.addEventListener('toggle', (event) => {
        const open = event.newState === 'open';
        this.button.setAttribute('aria-expanded', String(open));
        if (!open) return;
        this.track('shipping_countries_open');
        this.search.value = '';
        this.filter();
        // a phone keyboard would cover the bottom sheet: focus the field only with a fine pointer
        if (window.matchMedia('(pointer: fine)').matches) this.search.focus();
        this.list.querySelector('[aria-current]')?.scrollIntoView({ block: 'nearest' });
      }, { signal });
      // Without the popover API (Chrome < 114) the button toggles [open] instead and the CSS
      // in lab-section-header.css shows the list as a bottom sheet.
      this.native = Object.prototype.hasOwnProperty.call(HTMLElement.prototype, 'popover') && CSS.supports('selector(:popover-open)');
      if (!this.native) {
        // a build with the attribute but without :popover-open (early Chromium 113) would still
        // toggle and hide [popover] natively: drop both so only [open] decides
        this.button.removeAttribute('popovertarget');
        this.pop.removeAttribute('popover');
        this.button.addEventListener('click', () => this.setOpen(!this.pop.hasAttribute('open')), { signal });
        document.addEventListener('click', (event) => {
          if (this.pop.hasAttribute('open') && !this.pop.contains(event.target) && !this.button.contains(event.target)) this.setOpen(false);
        }, { signal });
        document.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !this.pop.hasAttribute('open')) return;
          this.setOpen(false);
          this.button.focus();
        }, { signal });
      }
      this.search.addEventListener('input', () => this.filter(), { signal });
      this.search.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        const first = [...this.list.children].find((item) => !item.hidden);
        if (first) this.pick(first.querySelector('[data-code]').dataset.code);
      }, { signal });
      this.list.addEventListener('click', (event) => {
        const code = event.target.closest('[data-code]')?.dataset.code;
        if (code) this.pick(code);
      }, { signal });
      this.rates.addEventListener('toggle', () => {
        if (this.rates.open) this.track('shipping_rates_open');
      }, { signal });
      this.pickInitial().then((code) => {
        // Slow IP detection must not replace a destination the visitor has already chosen,
        // or repaint an old quote after a variant change disconnected this element.
        if (!signal.aborted && !this.hasPickedCountry) this.render(code);
      });
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    /** The fallback's open/close, announced through the same toggle event the popover fires. */
    setOpen(open) {
      this.pop.toggleAttribute('open', open);
      this.pop.dispatchEvent(Object.assign(new Event('toggle'), { newState: open ? 'open' : 'closed' }));
    }

    pick(code) {
      this.hasPickedCountry = true;
      try {
        localStorage.setItem(STORAGE_KEY, code);
      } catch {
        // storage blocked: the pick still applies to this page
      }
      if (this.native) this.pop.hidePopover();
      else this.setOpen(false);
      this.render(code);
      this.button.focus();
      this.track('shipping_country_select');
    }

    track(name) {
      track(name, { shipping_country: this.current, item_id: String(this.data.variant) });
    }

    filter() {
      const query = fold(this.search.value.trim());
      // A complete country code is a destination, not a fragment of a translated name:
      // "DE" must select Germany rather than the first row containing "de" (for example,
      // "Città del Vaticano" in Italian). Name searches retain their partial matching.
      const exactCode = this.codes.find((code) => code.toLowerCase() === query);
      let shown = 0;
      for (const item of this.list.children) {
        item.hidden = exactCode
          ? item.querySelector('[data-code]').dataset.code !== exactCode
          : Boolean(query) && !item.dataset.search.includes(query);
        if (!item.hidden) shown++;
      }
      this.none.hidden = shown > 0;
    }

    /** One popover row: the country and what one item costs to ship there. */
    row(code) {
      const first = this.cost(this.data.card.countries[code], 1);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'loc__word loc__country draw';
      button.dataset.code = code;
      const name = button.appendChild(document.createElement('span'));
      name.className = 'loc__country-name';
      name.textContent = this.names.of(code);
      const cost = button.appendChild(document.createElement('span'));
      cost.className = 'loc__currency tnum';
      cost.textContent = !first ? '—' : first.high === 0 ? this.data.i18n.free : this.range(first.low, first.high);
      const item = document.createElement('li');
      item.className = 'loc__item';
      item.dataset.search = fold(`${this.names.of(code)} ${ENGLISH.of(code)} ${code}`);
      item.appendChild(button);
      return item;
    }

    async pickInitial() {
      const { card, lang, market } = this.data;
      const known = (code) => (code && card.countries[code] ? code : null);
      const saved = known(readSaved());
      if (saved) return saved;
      const ip = known(await detectCountry());
      // Italy is the default market and the IP of a visitor on a VPN or a travelling phone; a
      // German or French page is not shown Italian rates unless the visitor picks Italy
      const fromLanguage = lang !== 'it' ? known(LANGUAGE_COUNTRY[lang]) : null;
      if (ip && !(ip === 'IT' && fromLanguage)) return ip;
      return fromLanguage || known(market) || this.codes[0];
    }

    money(cents) {
      const { moneyFormat, shopCurrency, card, lang } = this.data;
      if (shopCurrency !== card.currency) return new Intl.NumberFormat(lang, { style: 'currency', currency: card.currency }).format(cents / 100);
      const amount = cents / 100;
      // Thousands grouping as fixed() in assets/lab-bottom-strip.js, so 123450 reads 1.234,50
      const fixed = (decimals, thousands, decimal) => {
        const [whole, fraction] = amount.toFixed(decimals).split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fraction ? `${grouped}${decimal}${fraction}` : grouped;
      };
      return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        if (key === 'amount_with_comma_separator') return fixed(2, '.', ',');
        if (key === 'amount_no_decimals') return fixed(0, ',', '.');
        if (key === 'amount_no_decimals_with_comma_separator') return fixed(0, '.', ',');
        return fixed(2, ',', '.');
      });
    }

    range(low, high) {
      return low === high ? this.money(low) : `${this.money(low)} – ${this.money(high)}`;
    }

    /** Lowest and highest rate across the country's zones for n items; null past the last band. */
    cost(zones, n) {
      const { grams, price, sameCurrency } = this.data;
      const weight = grams * n;
      const rates = zones.map((zone) => {
        if (sameCurrency && zone.free_from !== null && price * n >= zone.free_from) return 0;
        const fits = zone.bands.filter((b) => weight >= b.min && (b.max === null || weight <= b.max));
        return fits.length ? Math.min(...fits.map((b) => b.price)) : null;
      });
      if (rates.includes(null)) return null;
      return { low: Math.min(...rates), high: Math.max(...rates) };
    }

    render(code) {
      const zones = this.data.card.countries[code];
      const { i18n } = this.data;
      this.current = code;
      this.name.textContent = this.names.of(code);
      for (const b of this.list.querySelectorAll('[data-code]')) {
        if (b.dataset.code === code) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      }
      const runs = [];
      for (let n = 1; n <= MAX_QUANTITY; n++) {
        const c = this.cost(zones, n);
        if (!c) break;
        const last = runs.at(-1);
        if (last && last.low === c.low && last.high === c.high) last.to = n;
        else runs.push({ from: n, to: n, ...c });
        if (c.high === 0) {
          runs.at(-1).open = true;
          break;
        }
      }

      const first = runs[0];
      if (!first) this.quote.textContent = i18n.unknown;
      else if (first.high === 0) this.quote.textContent = i18n.free_one;
      else if (first.low === first.high) this.quote.textContent = i18n.one.replace('%price%', this.money(first.low));
      else this.quote.textContent = i18n.one_by_zone.replace('%low%', this.money(first.low)).replace('%high%', this.money(first.high));

      const freeFrom = this.data.sameCurrency && zones.every((z) => z.free_from !== null) ? Math.max(...zones.map((z) => z.free_from)) : null;
      this.free.hidden = freeFrom === null;
      if (freeFrom !== null) this.free.textContent = i18n.free_from.replace('%amount%', this.money(freeFrom));

      this.rates.hidden = runs.length < 2;
      this.body.replaceChildren(
        ...runs.map((run) => {
          const row = document.createElement('tr');
          const quantity = run.open ? `${run.from}+` : run.from === run.to ? `${run.from}` : `${run.from}–${run.to}`;
          const costText = run.high === 0 ? i18n.free : this.range(run.low, run.high);
          for (const text of [quantity, costText]) row.appendChild(document.createElement('td')).textContent = text;
          return row;
        }),
      );
    }
  }

  customElements.define('shipping-quote', ShippingQuote);
}
